import { inject, Injectable } from '@angular/core';
import { Consorcio, getCocheraLabel } from '@pages/consorcios/consorcios.config';
import { Consorcista, OccupancyRole } from '@pages/consorcios/consorcistas.service';
import { KeyRequestFormData, KeyRequestFormUnit, KeyRequestSubmit, KeyRequestSubmitResult } from '@pages/key-request-form/key-request-form.config';
import {
    isValidDni, KEY_REQUEST_KEY_TYPE_OPTIONS, KEY_REQUEST_REASON_OPTIONS, KEY_REQUEST_RELATION_OPTIONS,
    KeyRequest, KeyRequestConsorcistaRef, KeyRequestToken, keyRequestUnitText, normalizeDni,
} from './key-requests.config';
import { KeyRequestsService } from './key-requests.service';
import { ChatsService } from '@shared/services/chats.service';
import { getConnectionWsp } from 'src/app/models/user';
import { WspDisconnectedError, WspSendBlockedError } from 'src/app/models/wsp-send-errors';

// resultado de aceptar o rechazar: el pedido se resuelve aunque el mensaje no salga (se avisa en pantalla)
export interface KeyRequestResolveResult {
    resolved: boolean;
    // si no se pudo resolver (por ejemplo, ya estaba resuelto)
    error?: string;
    messageSent?: boolean;
    // por qué no salió el mensaje, para mostrarlo
    messageError?: string;
}

// el link dura 30 minutos (igual que en la function createformlink)
const TEST_TOKEN_MINUTES = 30;

/*
"Controlador" del pedido de copia de llave (AF298).
- loadForm: lo que necesita el formulario público al abrir el link (solo lo mínimo: nunca devuelve DNIs ni teléfonos).
- submitForm: valida todo de nuevo (no alcanza con lo que oculta la pantalla) y guarda en una sola escritura
  el pedido, los datos que faltaban y el token como usado. Si algo falla no se guarda nada y el link sigue sirviendo.
Si se aprueba la propuesta de que el formulario no toque Firestore, esta misma lógica pasa a la function del formulario.
*/
@Injectable({
    providedIn: 'root',
})
export class KeyRequestsManagerService {
    private service = inject(KeyRequestsService);
    private chatsService = inject(ChatsService);

    async loadForm(cod: string): Promise<KeyRequestFormData> {
        const check = await this.checkToken(cod);
        if (!check.token) {
            return { state: check.state };
        }
        const token = check.token;
        const docs = await this.getPersonConsorcistas(token);
        if (!docs.length) {
            return { state: 'invalid' };
        }
        const hasDni = docs.some(d => !!d.dni);
        const consorcios = new Map<string, Consorcio | undefined>();
        const units: KeyRequestFormUnit[] = [];
        for (const doc of docs) {
            if (!consorcios.has(doc.consorcioId!)) {
                consorcios.set(doc.consorcioId!, await this.service.getConsorcio(doc.consorcioId!));
            }
            const consorcio = consorcios.get(doc.consorcioId!);
            const members = await this.getUnitMembers(doc, token.wspId);
            units.push({
                consorcistaId: doc.uid!,
                consorcioId: doc.consorcioId!,
                consorcioName: consorcio?.name ?? '',
                label: await this.unitLabel(doc, consorcio),
                occupancyRole: doc.occupancyRole,
                hasDni,
                hasEmergencyContact: hasEmergencyContact(doc),
                members: members.map(m => ({ consorcistaId: m.uid!, firstName: m.firstName, lastName: m.lastName, hasDni: !!m.dni })),
            });
        }
        const first = docs[0];
        return {
            state: 'ok',
            requester: { firstName: first.firstName, lastName: first.lastName },
            units,
            preselectedConsorcistaId: token.preselected?.consorcistaId,
        };
    }

    async submitForm(cod: string, data: KeyRequestSubmit): Promise<KeyRequestSubmitResult> {
        const check = await this.checkToken(cod);
        if (!check.token) {
            return { success: false, error: check.state === 'used' ? 'USED' : check.state === 'expired' ? 'EXPIRED' : 'INVALID' };
        }
        const token = check.token;
        const invalid = (message: string): KeyRequestSubmitResult => ({ success: false, error: 'VALIDATION', message });

        // la unidad tiene que ser de esta persona en esta administración (está en el token)
        const ref = token.consorcistas.find(c => c.consorcistaId === data.consorcistaId);
        if (!ref) {
            return invalid('La unidad elegida no es válida');
        }
        const unitDoc = await this.service.getConsorcista(ref.consorcioId, ref.consorcistaId);
        const consorcio = await this.service.getConsorcio(ref.consorcioId);
        if (!unitDoc || !consorcio || consorcio.administrationUserId !== token.administrationId) {
            return invalid('La unidad elegida no es válida');
        }
        const personDocs = await this.getPersonConsorcistas(token);
        const existingDni = personDocs.find(d => !!d.dni)?.dni;
        const isRealEstate = unitDoc.occupancyRole === OccupancyRole.RealEstate;

        // valores de las listas
        if (!KEY_REQUEST_KEY_TYPE_OPTIONS.some(o => o.value === data.keyType)) return invalid('El tipo de llave no es válido');
        if (data.keyType === 'other' && !text(data.keyTypeOther)) return invalid('Falta aclarar el tipo de llave');
        if (!KEY_REQUEST_REASON_OPTIONS.some(o => o.value === data.reason)) return invalid('El motivo no es válido');
        if (isRealEstate && !isValidDni(data.pickupResponsibleDni)) return invalid('El DNI de quien retira la llave no es válido');

        const r = data.recipient;
        if (r.type !== 'self') {
            if (!KEY_REQUEST_RELATION_OPTIONS.some(o => o.value === r.relation)) return invalid('El vínculo no es válido');
            if (r.relation === 'other' && !text(r.relationOther)) return invalid('Falta aclarar el vínculo');
        }

        // destinatario
        let recipient: KeyRequest['recipient'];
        let memberDoc: Consorcista | undefined;
        switch (r.type) {
            case 'self': {
                if (isRealEstate) return invalid('Una inmobiliaria no puede pedir la llave para sí misma');
                const dni = existingDni ?? normalizeDni(r.dni);
                if (!isValidDni(dni)) return invalid('El DNI no es válido');
                recipient = clean({
                    type: 'self', consorcistaId: unitDoc.uid, firstName: unitDoc.firstName, lastName: unitDoc.lastName, dni,
                    phone: phoneOf(unitDoc),
                });
                break;
            }
            case 'registered': {
                memberDoc = r.consorcistaId ? await this.service.getConsorcista(ref.consorcioId, r.consorcistaId) : undefined;
                // tiene que ser de la misma unidad, no inmobiliaria y no la misma persona
                if (!memberDoc || !sameUnit(memberDoc, unitDoc) || memberDoc.uid === unitDoc.uid || memberDoc.wspId === token.wspId
                    || memberDoc.occupancyRole === OccupancyRole.RealEstate || memberDoc.onlyGarage) {
                    return invalid('La persona elegida no es válida');
                }
                const dni = memberDoc.dni ?? normalizeDni(r.dni);
                if (!isValidDni(dni)) return invalid('El DNI no es válido');
                recipient = clean({
                    type: 'registered', consorcistaId: memberDoc.uid, firstName: memberDoc.firstName, lastName: memberDoc.lastName, dni,
                    phone: phoneOf(memberDoc), relation: r.relation, relationOther: r.relation === 'other' ? text(r.relationOther) : undefined,
                });
                break;
            }
            case 'unregistered': {
                if (!text(r.firstName) || !text(r.lastName)) return invalid('Falta el nombre o el apellido');
                if (!text(r.phone?.phoneArea) || !text(r.phone?.phoneNumber)) return invalid('Falta el teléfono');
                if (!isValidDni(r.dni)) return invalid('El DNI no es válido');
                recipient = clean({
                    type: 'unregistered', firstName: text(r.firstName), lastName: text(r.lastName), dni: normalizeDni(r.dni),
                    phone: { countryCode: text(r.phone?.countryCode) || '54', phoneArea: text(r.phone?.phoneArea), phoneNumber: text(r.phone?.phoneNumber) },
                    relation: r.relation, relationOther: r.relation === 'other' ? text(r.relationOther) : undefined,
                });
                break;
            }
            default:
                return invalid('Falta elegir para quién es la llave');
        }

        // contacto de emergencia: solo si la unidad no lo tiene
        const needsEmergency = !hasEmergencyContact(unitDoc);
        const em = data.emergencyContact;
        if (needsEmergency && (!em || !text(em.firstName) || !text(em.lastName) || !text(em.phoneArea) || !text(em.phoneNumber) || !text(em.relation))) {
            return invalid('Faltan datos del contacto de emergencia');
        }

        const keyRequest: KeyRequest = clean({
            envioId: token.cod,
            canal: 'Whatsapp',
            wspId: token.wspId,
            administrationUserId: token.administrationId,
            consorcioId: ref.consorcioId,
            consorcioName: consorcio.name ?? '',
            unit: clean({ tower: unitDoc.tower, floor: unitDoc.floor, apartment: unitDoc.apartment, label: await this.unitLabel(unitDoc, consorcio) }),
            requester: clean({ consorcistaId: unitDoc.uid!, firstName: unitDoc.firstName, lastName: unitDoc.lastName, occupancyRole: unitDoc.occupancyRole }),
            recipient,
            pickupResponsibleDni: isRealEstate ? normalizeDni(data.pickupResponsibleDni) : undefined,
            keyType: data.keyType,
            keyTypeOther: data.keyType === 'other' ? text(data.keyTypeOther) : undefined,
            reason: data.reason,
            lostKey: data.reason === 'replacement' && !!data.lostKey,
            status: 'pending',
            createdAt: new Date(),
        });

        const writes: { path: string, data: any, mode: 'set' | 'update' }[] = [
            { path: this.service.getKeyRequestsPath(ref.consorcioId) + '/' + this.service.newKeyRequestId(ref.consorcioId), data: keyRequest, mode: 'set' },
            { path: this.service.getTokenPath(token.cod), data: { status: 'used', usedAt: new Date() }, mode: 'update' },
        ];
        // DNI propio: en todas las unidades de la persona en esta administración, solo si no lo tenía
        if (r.type === 'self' && !existingDni) {
            personDocs.forEach(d => writes.push({ path: this.service.getConsorcistaPath(d.consorcioId!, d.uid!), data: { dni: recipient.dni }, mode: 'update' }));
        }
        // DNI de la persona registrada elegida: en su registro, solo si no lo tenía
        if (memberDoc && !memberDoc.dni) {
            writes.push({ path: this.service.getConsorcistaPath(ref.consorcioId, memberDoc.uid!), data: { dni: recipient.dni }, mode: 'update' });
        }
        if (needsEmergency && em) {
            writes.push({
                path: this.service.getConsorcistaPath(ref.consorcioId, unitDoc.uid!),
                data: {
                    emergencyContact: {
                        firstName: text(em.firstName), lastName: text(em.lastName), countryCode: text(em.countryCode) || '54',
                        phoneArea: text(em.phoneArea), phoneNumber: text(em.phoneNumber), relation: text(em.relation), accessKey: !!em.accessKey,
                    },
                },
                mode: 'update',
            });
        }

        try {
            await this.service.commit(writes);
        } catch (error) {
            console.error('[pedido-llave] error al guardar el pedido', error);
            return { success: false, error: 'INTERNAL_ERROR' };
        }
        return { success: true };
    }

    /*
    Crea un link de prueba igual al que genera la function createformlink (type keyRequest), para probar el formulario
    sin el agente. Recibe un consorcista (consorcio + id); toma su número y busca sus unidades en la misma administración.
    Escribe en Firestore: usar solo en test y con consorcistas de prueba propios. Devuelve el link.
    */
    async createTestToken(consorcioId: string, consorcistaId: string, preselect = false): Promise<string> {
        const consorcista = await this.service.getConsorcista(consorcioId, consorcistaId);
        const consorcio = await this.service.getConsorcio(consorcioId);
        if (!consorcista?.wspId || !consorcio) {
            throw new Error('No existe el consorcista o no tiene wspId');
        }
        const administrationId = consorcio.administrationUserId;
        const consorcistas: KeyRequestConsorcistaRef[] = [];
        for (const id of await this.service.getAdministrationConsorcioIds(administrationId)) {
            (await this.service.getConsorcistaIdsByWspId(id, consorcista.wspId)).forEach(cid => consorcistas.push({ consorcioId: id, consorcistaId: cid }));
        }
        const now = new Date();
        const previous = (await this.service.getActiveTokens(consorcista.wspId)).filter(t => t.administrationId === administrationId);
        const bytes = crypto.getRandomValues(new Uint8Array(16));
        const cod = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        const token: KeyRequestToken = clean({
            cod,
            administrationId,
            wspId: consorcista.wspId,
            consorcistas,
            preselected: preselect ? { consorcioId, consorcistaId } : undefined,
            status: 'active',
            createdAt: now,
            expiresAt: new Date(now.getTime() + TEST_TOKEN_MINUTES * 60 * 1000),
        });
        await this.service.commit([
            ...previous.map(t => ({ path: this.service.getTokenPath(t.cod), data: { status: 'invalidated', invalidatedAt: now }, mode: 'update' as const })),
            { path: this.service.getTokenPath(cod), data: token, mode: 'set' },
        ]);
        return window.location.origin + '/pedido-llave?cod=' + cod;
    }

    /* ---------- pantalla de la administración ---------- */

    /*
    Acepta el pedido y le avisa al solicitante por WhatsApp, con las instrucciones de retiro del consorcio (solapa "Llaves").
    Por ahora el aviso es texto libre por enviar-mensaje: solo llega si el consorcista escribió en las últimas 24 horas.
    Cuando esté la plantilla de Meta, cambiar sendResolutionMessage.
    */
    async accept(request: KeyRequest, currentUser: any): Promise<KeyRequestResolveResult> {
        const resolved = await this.resolve(request, { status: 'accepted', resolvedAt: new Date() });
        if (resolved.error) {
            return resolved;
        }
        const consorcio = await this.service.getConsorcio(request.consorcioId);
        const instructions = consorcio?.keyService?.instructions?.trim();
        const content = `Hola ${request.requester.firstName}. La administración aceptó tu pedido de copia de llave para ${keyRequestUnitText(request)} y lo va a tramitar con la empresa de llaves.`
            + (instructions ? `\n\nInstrucciones: ${instructions}` : '');
        return { ...resolved, ...(await this.sendResolutionMessage(request, content, currentUser)) };
    }

    // rechaza el pedido con el motivo (obligatorio) y se lo avisa al solicitante por WhatsApp
    async reject(request: KeyRequest, reason: string, currentUser: any): Promise<KeyRequestResolveResult> {
        const rejectionReason = text(reason);
        if (!rejectionReason) {
            return { resolved: false, error: 'Falta el motivo del rechazo' };
        }
        const resolved = await this.resolve(request, { status: 'rejected', rejectionReason, resolvedAt: new Date() });
        if (resolved.error) {
            return resolved;
        }
        const content = `Hola ${request.requester.firstName}. La administración rechazó tu pedido de copia de llave para ${keyRequestUnitText(request)}. Motivo: ${rejectionReason}`;
        return { ...resolved, ...(await this.sendResolutionMessage(request, content, currentUser)) };
    }

    /* ---------- internos ---------- */

    // solo se resuelve un pedido pendiente (se vuelve a leer por si otra pestaña ya lo resolvió)
    private async resolve(request: KeyRequest, data: Partial<KeyRequest>): Promise<KeyRequestResolveResult> {
        const current = await this.service.getKeyRequest(request.consorcioId, request.uid!);
        if (!current) {
            return { resolved: false, error: 'No se encontró el pedido' };
        }
        if (current.status !== 'pending') {
            return { resolved: false, error: 'El pedido ya estaba resuelto' };
        }
        await this.service.updateKeyRequest(request.consorcioId, request.uid!, data);
        return { resolved: true };
    }

    // mismo envío que los avisos de consorcistas pendientes y consultas externas (reactiva al agente para ese número)
    private async sendResolutionMessage(request: KeyRequest, content: string, currentUser: any): Promise<Pick<KeyRequestResolveResult, 'messageSent' | 'messageError'>> {
        if (currentUser?.subscription === 'Free' || !currentUser?.subscription) {
            return { messageSent: false, messageError: 'tu plan no incluye mensajes de WhatsApp' };
        }
        try {
            const response = await this.chatsService.prepareUnmuteWithMessage(request.wspId, {
                content,
                timestamp: new Date(),
                type: 'administration',
                collection: 'keyRequests',
                collectionId: request.uid,
            }, { instanceName: currentUser['administrationWsp'] }, getConnectionWsp(currentUser));
            if (!response?.success) {
                return { messageSent: false, messageError: 'WhatsApp no aceptó el mensaje (si pasaron más de 24 horas desde el último mensaje del consorcista, hace falta la plantilla)' };
            }
            return { messageSent: true };
        } catch (error) {
            if (error instanceof WspSendBlockedError) {
                return { messageSent: false, messageError: error instanceof WspDisconnectedError ? 'tu WhatsApp está desconectado' : 'no tenés saldo de mensajes' };
            }
            console.error('[pedidos-llave] error al enviar el aviso', error);
            return { messageSent: false, messageError: 'no se pudo enviar el mensaje' };
        }
    }


    private async checkToken(cod: string): Promise<{ state: 'ok' | 'invalid' | 'expired' | 'used', token?: KeyRequestToken }> {
        const token = cod ? await this.service.getToken(cod) : undefined;
        if (!token) return { state: 'invalid' };
        if (token.status === 'used') return { state: 'used' };
        if (token.status !== 'active') return { state: 'invalid' };
        if (token.expiresAt.getTime() < Date.now()) return { state: 'expired' };
        return { state: 'ok', token };
    }

    // documentos de consorcista de la persona en esta administración (los del token que todavía existen)
    private async getPersonConsorcistas(token: KeyRequestToken): Promise<Consorcista[]> {
        const docs: Consorcista[] = [];
        for (const ref of token.consorcistas ?? []) {
            const doc = await this.service.getConsorcista(ref.consorcioId, ref.consorcistaId);
            if (doc) docs.push(doc);
        }
        return docs;
    }

    // otras personas registradas en la misma unidad: sin inmobiliarias, sin la misma persona y sin "solo cochera"
    private async getUnitMembers(unitDoc: Consorcista, wspId: string): Promise<Consorcista[]> {
        if (unitDoc.onlyGarage || !unitDoc.floor || !unitDoc.apartment) {
            return [];
        }
        const list = await this.service.getUnitConsorcistas(unitDoc.consorcioId!, String(unitDoc.floor), String(unitDoc.apartment));
        return list.filter(m => sameUnit(m, unitDoc) && m.uid !== unitDoc.uid && m.wspId !== wspId
            && m.occupancyRole !== OccupancyRole.RealEstate && !m.onlyGarage);
    }

    private async unitLabel(doc: Consorcista, consorcio: Consorcio | undefined): Promise<string> {
        if (doc.onlyGarage) {
            const cochera = (await this.service.getCocheras(doc.consorcioId!, doc.uid!))[0];
            if (!cochera) return 'Cochera';
            return 'Cochera ' + (cochera.label || getCocheraLabel(consorcio?.cocheras, cochera.nivel, cochera.numero) || cochera.numero);
        }
        const parts = [
            doc.tower ? 'Torre ' + doc.tower : '',
            doc.floor ? 'Piso ' + doc.floor : '',
            doc.apartment ? 'Depto ' + doc.apartment : '',
        ].filter(Boolean);
        return parts.join(' - ') || 'Unidad';
    }
}

function text(value: unknown): string {
    return String(value ?? '').trim();
}

function hasEmergencyContact(doc: Consorcista): boolean {
    return !!doc.emergencyContact?.firstName;
}

function sameUnit(a: Consorcista, b: Consorcista): boolean {
    return String(a.floor ?? '') === String(b.floor ?? '') && String(a.apartment ?? '') === String(b.apartment ?? '')
        && String(a.tower ?? '') === String(b.tower ?? '');
}

function phoneOf(doc: Consorcista) {
    if (!doc.phoneArea && !doc.phoneNumber) return undefined;
    return { countryCode: String(doc.countryCode ?? '54'), phoneArea: String(doc.phoneArea ?? ''), phoneNumber: String(doc.phoneNumber ?? '') };
}

// Firestore no acepta undefined: se sacan los campos sin valor (un nivel)
function clean<T>(obj: T): T {
    return Object.fromEntries(Object.entries(obj as any).filter(([, v]) => v !== undefined)) as T;
}
